// Една карта от веригата-равенство на правилото: [приоритет] [количество]
// [мярка] [продукт] … [↑ ↓] [✕]. Между картите веригата рисува „=" — заедно
// се четат като изречение: „1 кг Кафе A = 0.7 кг Кафе B".
//
// Глупав рендер: клавишите (↑/↓/Enter между количествата, Alt+↑/↓ мести
// картата) и плъзгането се решават във веригата (`InterchangeRuleChain`).
// Полето за количество носи id `amountInputId(key)` — по него веригата мести
// фокуса.
import type { DragEvent, KeyboardEvent } from "react";
import { InputNumber } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import {
  amountFormatter,
  amountInputId,
  amountParser,
  type InterchangeMemberDraft,
} from "./interchangeablesShared";

const ICON_BUTTON_CLASS =
  "flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-faint outline-none hover:bg-fill hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--color-brand)] disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent";

type Props = {
  member: InterchangeMemberDraft;
  category: string | null;
  // номерът на приоритета (режим „По реда тук"); null — без номер
  priority: { number: number | null } | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
  // стрелките ↑/↓ и плъзгането — само в режим „По реда тук"
  reorderable: boolean;
  dragging: boolean;
  dropTarget: boolean;
  onAmountChange: (value: number | null) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  // id-тата на стрелките — веригата връща фокуса на тях след преместване
  moveButtonIds: { up: string; down: string };
  onRemove: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
};

export const InterchangeMemberCard = ({
  member,
  category,
  priority,
  canMoveUp,
  canMoveDown,
  reorderable,
  dragging,
  dropTarget,
  onAmountChange,
  onMoveUp,
  onMoveDown,
  moveButtonIds,
  onRemove,
  onKeyDown,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const invalid = member.amount == null || !(member.amount > 0);

  return (
    <div
      data-ich-card={member.key}
      onKeyDown={onKeyDown}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`flex items-center rounded-[10px] border bg-surface focus-within:border-brand ${
        isNarrow ? "gap-2 px-2.5 py-2" : "gap-3 px-3.5 py-2.5"
      } ${dropTarget ? "border-brand bg-brand-tint" : "border-shade-3"} ${dragging ? "opacity-50" : ""}`}
    >
      {priority && (
        // Номерът е и дръжката за плъзгане (мишката) — клавиатурата мести
        // картата със стрелките или Alt+↑/↓.
        <span
          draggable={reorderable}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          title={reorderable ? t("inventoryPage.ichDragHandle") : undefined}
          aria-label={
            priority.number != null
              ? t("inventoryPage.ichPriorityAria", { index: priority.number })
              : t("inventoryPage.ichPriorityNone")
          }
          className={`flex h-7 w-7 shrink-0 select-none items-center justify-center rounded-full font-sheet text-[12.5px] font-bold leading-none ${
            priority.number != null ? "bg-brand-tint text-brand-deep-ink" : "bg-fill-2 text-faint"
          } ${reorderable ? "cursor-grab active:cursor-grabbing" : ""}`}
        >
          {priority.number ?? "·"}
        </span>
      )}

      <InputNumber
        id={amountInputId(member.key)}
        value={member.amount}
        onChange={(value) => onAmountChange(value == null ? null : Number(value))}
        min={0}
        max={99999}
        controls={false}
        // ↑/↓ местят фокуса между количествата (веригата), не стъпват числото
        keyboard={false}
        placeholder="1"
        formatter={amountFormatter}
        parser={amountParser}
        status={invalid ? "error" : undefined}
        aria-label={t("inventoryPage.ichAmountAria", { name: member.productName })}
        // числото е акцентът на картата — моно, вдясно (16px на телефон: iOS зуум)
        className={`shrink-0 [&_input]:!text-right [&_input]:!font-sheet [&_input]:!font-semibold ${
          isNarrow ? "!w-[84px] [&_input]:!text-base" : "!w-[104px] [&_input]:!text-[15px]"
        }`}
      />
      <span
        title={member.unit ?? undefined}
        className={`shrink-0 truncate text-[13px] leading-[normal] text-muted ${
          isNarrow ? "w-[40px]" : "w-[60px]"
        }`}
      >
        {member.unit ?? ""}
      </span>

      <div className="min-w-0 flex-1">
        <div
          className={`text-[15px] font-semibold leading-[1.3] text-ink ${
            isNarrow ? "break-words" : "truncate"
          }`}
        >
          {member.productName}
        </div>
        {category && (
          <div className="truncate text-xs leading-[normal] text-faint">{category}</div>
        )}
      </div>

      {reorderable && (
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            id={moveButtonIds.up}
            onClick={onMoveUp}
            disabled={!canMoveUp}
            aria-label={t("inventoryPage.ichMoveUp")}
            title={`${t("inventoryPage.ichMoveUp")} (Alt+↑)`}
            className={ICON_BUTTON_CLASS}
          >
            <ArrowUpOutlined className="text-[13px]" />
          </button>
          <button
            type="button"
            id={moveButtonIds.down}
            onClick={onMoveDown}
            disabled={!canMoveDown}
            aria-label={t("inventoryPage.ichMoveDown")}
            title={`${t("inventoryPage.ichMoveDown")} (Alt+↓)`}
            className={ICON_BUTTON_CLASS}
          >
            <ArrowDownOutlined className="text-[13px]" />
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("inventoryPage.ichRemove", { name: member.productName })}
        title={t("inventoryPage.ichRemove", { name: member.productName })}
        className={`${ICON_BUTTON_CLASS} hover:!bg-danger-tint hover:!text-danger-ink`}
      >
        <CloseOutlined className="text-[13px]" />
      </button>
    </div>
  );
};

export default InterchangeMemberCard;
